import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import type { PageMeta } from '@/types/core/page-meta'
import { setupRouterGuards, vendorRouteGuard } from './guards'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: {
        title: '首頁',
        subtitle: 'Reward Swap 健康集點通歡迎頁面'
      } as PageMeta
    },

    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../views/DashboardView.vue'),
      meta: {
        title: '儀表板',
        subtitle: '系統概況與統計資訊',
        breadcrumbs: [
          { label: '首頁', to: '/' },
          { label: '儀表板' }
        ]
      } as PageMeta
    },

    // 統一登入頁面
    {
      path: '/login',
      name: 'login',
      component: () => import('../components/layout/EmptyLayout.vue'),
      meta: {
        title: '登入',
        subtitle: '選擇主辦單位並使用 LINE 登入'
      } as PageMeta,
      children: [
        {
          path: '',
          name: 'channel-selection',
          component: () => import('../views/auth/UserLoginView.vue'),
          meta: {
            title: '登入',
            subtitle: '選擇主辦單位並使用 LINE 登入'
          } as PageMeta
        }
      ]
    },

    // 管理後台登入頁面
    {
      path: '/admin/login',
      component: () => import('../components/layout/EmptyLayout.vue'),
      meta: {
        title: '管理後台登入',
        subtitle: 'Reward Swap 健康集點通管理系統'
      } as PageMeta,
      children: [
        {
          path: '',
          name: 'admin-login',
          component: () => import('../views/auth/AdminLoginView.vue'),
          meta: {
            title: '管理後台登入'
          } as PageMeta
        }
      ]
    },

    // LINE OAuth 回調處理
    {
      path: '/line/callback',
      component: () => import('../components/layout/EmptyLayout.vue'),
      meta: {
        title: '登入處理中',
        requiresAuth: false
      } as PageMeta,
      children: [
        {
          path: '',
          name: 'line-callback',
          component: () => import('../views/auth/LineCallbackView.vue'),
          meta: {
            title: '登入處理中'
          } as PageMeta
        }
      ]
    },

    // 使用者註冊
    {
      path: '/register',
      component: () => import('../components/layout/EmptyLayout.vue'),
      meta: {
        title: '使用者註冊',
        requiresAuth: false
      } as PageMeta,
      children: [
        {
          path: '',
          name: 'register',
          component: () => import('../views/auth/RegisterView.vue'),
          meta: {
            title: '使用者註冊',
            subtitle: '完成個人資料註冊'
          } as PageMeta
        }
      ]
    },

    // Mock 開發用路由 (開發階段保留) - 一般用戶
    {
      path: '/mock-user-selection',
      component: () => import('../components/layout/EmptyLayout.vue'),
      meta: {
        title: '選擇模擬用戶',
        requiresAuth: false
      } as PageMeta,
      children: [
        {
          path: '',
          name: 'mock-user-selection',
          component: () => import('../views/auth/UserSelectionView.vue'),
          meta: {
            title: '選擇模擬用戶',
            subtitle: '選擇一個用戶身份進行模擬登入'
          } as PageMeta
        }
      ]
    },
    // Mock 開發用路由 (開發階段保留) - 管理後台
    {
      path: '/admin/mock-user-selection',
      component: () => import('../components/layout/EmptyLayout.vue'),
      meta: {
        title: '選擇測試身份',
        requiresAuth: false
      } as PageMeta,
      children: [
        {
          path: '',
          name: 'admin-mock-user-selection',
          component: () => import('../views/admin/RoleSelectionView.vue'),
          meta: {
            title: '選擇測試身份',
            subtitle: '選擇角色以模擬不同權限的使用者登入'
          } as PageMeta
        }
      ]
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('../components/layout/AdminLayout.vue'),
      beforeEnter: (to, _from, next) => {
        if (to.path === '/admin') {
          // 動態導入 authService 來避免循環引用
          import('@/services/authService').then(({ authService }) => {
            const user = authService.getCurrentUser()
            
            // 未登入用戶導向管理後台登入頁
            if (!user) {
              next('/admin/login')
              return
            }
            
            // 已登入用戶導向預設路由
            const defaultRoute = authService.getDefaultRoute()
            if (defaultRoute !== '/admin') {
              next(defaultRoute)
            } else {
              // 預設導向到商品管理頁面
              next('/admin/vendor/products')
            }
          })
        } else {
          next()
        }
      },
      meta: {
        title: '管理後台',
        subtitle: '系統管理與設定'
      } as PageMeta,
      children: [
        {
          path: 'vendor/products',
          name: 'vendor-products',
          component: () => import('../views/admin/vendor/products/IndexView.vue'),
          meta: {
            title: '商品管理',
            subtitle: '管理商品列表',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '商品管理' }
            ]
          } as PageMeta
        },
        {
          path: 'vendor/products/create',
          name: 'vendor-products-create',
          component: () => import('../views/admin/vendor/products/CreateView.vue'),
          meta: {
            title: '新增商品',
            subtitle: '建立新的商品資訊',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '商品管理', to: '/admin/vendor/products' },
              { label: '新增商品' }
            ]
          } as PageMeta
        },
        {
          path: 'vendor/products/:id',
          name: 'vendor-products-detail',
          component: () => import('../views/admin/vendor/products/DetailView.vue'),
          meta: {
            title: '商品詳情',
            subtitle: '檢視商品完整資訊',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '商品管理', to: '/admin/vendor/products' },
              { label: '商品詳情' }
            ]
          } as PageMeta
        },
        {
          path: 'vendor/products/:id/edit',
          name: 'vendor-products-edit',
          component: () => import('../views/admin/vendor/products/EditView.vue'),
          meta: {
            title: '編輯商品',
            subtitle: '修改商品資訊',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '商品管理', to: '/admin/vendor/products' },
              { label: '編輯商品' }
            ]
          } as PageMeta
        },
        {
          path: 'vendor/profile',
          name: 'vendor-profile',
          component: () => import('../views/admin/vendor/ProfileView.vue'),
          meta: {
            title: '基本資料設定',
            subtitle: '維護店家基本資訊，確保資料的準確性',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '基本資料設定' }
            ],
            actions: [
              { 
                label: '下載店家 QRCode', 
                action: 'downloadQRCode', 
                type: 'outline', 
                icon: 'download' 
              }
            ]
          } as PageMeta
        },
        {
          path: 'vendor/staff',
          name: 'vendor-staff',
          component: () => import('../views/admin/vendor/StaffView.vue'),
          meta: {
            title: '共管人員設定',
            subtitle: '設定店家共管人員',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '共管人員設定' }
            ]
          } as PageMeta
        },
        {
          path: 'vendor/reports/exchange',
          name: 'vendor-reports-exchange',
          component: () => import('../views/admin/vendor/reports/VendorExchangeReport.vue'),
          meta: {
            title: '點數兌換報表',
            subtitle: '查看店家點數兌換記錄',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '點數兌換報表' }
            ]
          } as PageMeta
        },
        {
          path: 'channel/vendors',
          name: 'channel-vendors',
          component: () => import('../views/admin/channel/VendorView.vue'),
          meta: {
            title: '店家管理',
            subtitle: '主辦單位管理合作店家',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '店家管理' }
            ]
          } as PageMeta
        },
        {
          path: 'channel/vendors/:id/review',
          name: 'channel-vendor-review',
          component: () => import('../views/admin/channel/VendorReviewView.vue'),
          meta: {
            title: '審核店家申請',
            subtitle: '檢視並審核店家申請資料',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '店家管理', to: '/admin/channel/vendors' },
              { label: '審核申請' }
            ]
          } as PageMeta
        },
        {
          path: 'channel/products/review',
          name: 'channel-products-review',
          component: () => import('../views/admin/channel/products/ReviewView.vue'),
          meta: {
            title: '商品審核管理',
            subtitle: '主辦單位審核店家商品上架申請',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '商品審核管理' }
            ]
          } as PageMeta
        },
        {
          path: 'channel/products/review/:id',
          name: 'channel-products-detail',
          component: () => import('../views/admin/channel/products/DetailView.vue'),
          meta: {
            title: '商品審核檢視',
            subtitle: '檢視商品詳細資訊並執行審核操作',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '商品審核管理', to: '/admin/channel/products/review' },
              { label: '商品檢視' }
            ]
          } as PageMeta
        },
        {
          path: 'channel/reports/exchange',
          name: 'channel-reports-exchange',
          component: () => import('../views/admin/channel/reports/ChannelExchangeReport.vue'),
          meta: {
            title: '點數兌換報表',
            subtitle: '查看主辦單位點數兌換記錄',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '點數兌換報表' }
            ]
          } as PageMeta
        },
        {
          path: 'channel/reports/revenue',
          name: 'channel-reports-revenue',
          component: () => import('../views/admin/channel/reports/ChannelRevenueReportSimple.vue'),
          meta: {
            title: '商家營收報表',
            subtitle: '查看主辦單位下所有商家的營收統計',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '商家營收報表' }
            ]
          } as PageMeta
        },
        {
          path: 'super-admin/channels',
          name: 'super-admin-channels',
          component: () => import('../views/admin/super-admin/ChannelView.vue'),
          meta: {
            title: '主辦單位資訊管理',
            subtitle: '最高管理者維護主辦單位資料',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '主辦單位管理' }
            ]
          } as PageMeta
        },
        {
          path: 'super-admin/staff',
          name: 'super-admin-staff',
          component: () => import('../views/admin/super-admin/StaffView.vue'),
          meta: {
            title: '共管人員設定',
            subtitle: '設定最高管理者共管人員',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '共管人員設定' }
            ]
          } as PageMeta
        },
        {
          path: 'super-admin/logs',
          name: 'super-admin-logs',
          component: () => import('../views/admin/LogManagementView.vue'),
          meta: {
            title: '日誌管理',
            subtitle: '瀏覽和下載系統日誌檔案',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '日誌管理' }
            ]
          } as PageMeta
        },
        {
          path: 'vendor-application',
          name: 'vendor-application',
          component: () => import('../views/admin/vendor/VendorApplicationView.vue'),
          meta: {
            title: '申請成為店家',
            subtitle: '填寫店家基本資料，等待主辦單位審核',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '申請成為店家' }
            ]
          } as PageMeta
        },
        {
          path: 'register',
          name: 'admin-register',
          component: () => import('../views/auth/RegisterView.vue'),
          meta: {
            title: '管理者註冊',
            subtitle: '完成管理者個人資料註冊',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: '管理後台', to: '/admin' },
              { label: '管理者註冊' }
            ],
            requiresAuth: false
          } as PageMeta
        }
      ]
    },

    // 前台用戶頁面 - Mobile App 風格
    {
      path: '/vendors',
      name: 'vendors',
      component: () => import('../views/VendorsView.vue'),
      meta: {
        title: '店家列表',
        subtitle: '瀏覽所有合作店家'
      } as PageMeta
    },
    {
      path: '/vendors/:id',
      name: 'vendor-detail',
      component: () => import('../views/VendorDetailView.vue'),
      meta: {
        title: '店家詳情',
        subtitle: '店家資訊與商品'
      } as PageMeta
    },
    {
      path: '/vendors/:vendorId/products/:productId',
      name: 'vendor-product-detail',
      component: () => import('../views/ProductDetailView.vue'),
      meta: {
        title: '商品詳情',
        subtitle: '商品完整資訊與購買選項',
        breadcrumbs: [
          { label: '首頁', to: '/' },
          { label: '店家列表', to: '/vendors' },
          { label: '店家詳情', to: (route) => `/vendors/${route.params.vendorId}` },
          { label: '商品詳情' }
        ]
      } as PageMeta
    },
    {
      path: '/products/:id',
      name: 'product-detail-redirect',
      redirect: async (to) => {
        // 這裡需要查詢商品所屬的店家，然後重導向到層級式 URL
        // 暫時先直接顯示商品頁面，後續可以改為重導向邏輯
        return { name: 'product-detail-standalone', params: { id: to.params.id } }
      }
    },
    {
      path: '/products/:id/standalone',
      name: 'product-detail-standalone',
      component: () => import('../views/ProductDetailView.vue'),
      meta: {
        title: '商品詳情',
        subtitle: '商品完整資訊與購買選項'
      } as PageMeta
    },
    {
      path: '/exchange',
      name: 'exchange',
      component: () => import('../views/ExchangeView.vue'),
      meta: {
        title: '商品兌換',
        subtitle: '使用點數兌換商品'
      } as PageMeta
    },
    {
      path: '/cart',
      name: 'cart',
      component: () => import('../views/user/CartView.vue'),
      meta: {
        title: '購物車',
        subtitle: '確認兌換商品'
      } as PageMeta
    },
    
    // 結帳流程路由
    {
      path: '/checkout/:vendorId',
      name: 'checkout',
      component: () => import('../views/user/CheckoutView.vue'),
      meta: {
        title: '結帳',
        subtitle: '確認商品與點數'
      } as PageMeta
    },
    {
      path: '/order/:txnSeq/qrcode',
      name: 'order-qrcode',
      component: () => import('../views/user/OrderQRCodeView.vue'),
      meta: {
        title: '訂單 QRCode',
        subtitle: '出示 QRCode 完成交易'
      } as PageMeta
    },
    
    // 店家操作路由 - 直接導向商品管理頁面
    {
      path: '/vendor',
      redirect: '/admin/vendor/products',
      beforeEnter: vendorRouteGuard,
      meta: {
        title: '店家操作中心',
        subtitle: '店家服務功能總覽'
      } as PageMeta
    },
    {
      path: '/vendor/scan',
      name: 'vendor-scan',
      component: () => import('../views/vendor/VendorScanView.vue'),
      beforeEnter: vendorRouteGuard,
      meta: {
        title: '訂單掃描',
        subtitle: '掃描客戶 QRCode'
      } as PageMeta
    },
    {
      path: '/vendor/no-line-transaction',
      name: 'vendor-no-line-transaction',
      component: () => import('../views/vendor/NoLineTransactionView.vue'),
      // beforeEnter: vendorRouteGuard, // 暫時移除用於測試
      meta: {
        title: '無 LINE 帳號交易',
        subtitle: '協助無 LINE 帳號的用戶完成商品兌換'
      } as PageMeta
    },
    {
      path: '/vendor/orders',
      name: 'vendor-orders',
      component: () => import('../views/vendor/VendorOrdersView.vue'),
      beforeEnter: vendorRouteGuard,
      meta: {
        title: '店家訂單查詢',
        subtitle: '查看店家已完成的訂單記錄'
      } as PageMeta
    },
    {
      path: '/user/profile',
      name: 'user-profile',
      component: () => import('../views/user/ProfileView.vue'),
      meta: {
        title: '我的',
        subtitle: '個人資料與設定'
      } as PageMeta
    },
    {
      path: '/user/points/history',
      name: 'user-points-history',
      component: () => import('../views/user/PointsHistoryView.vue'),
      meta: {
        title: '點數歷史',
        subtitle: '查看點數異動記錄'
      } as PageMeta
    },
    {
      path: '/user/points/transfer',
      name: 'user-points-transfer',
      component: () => import('../views/user/PointsTransferView.vue'),
      beforeEnter: (to, from, next) => {
        // 檢查點數轉移功能是否啟用
        import('@/config/featureFlags').then(({ features }) => {
          if (features.isPointsTransferEnabled()) {
            next()
          } else {
            // 功能未啟用，導向首頁或顯示錯誤頁面
            next('/')
          }
        })
      },
      meta: {
        title: '點數轉移',
        subtitle: '轉移點數給其他用戶'
      } as PageMeta
    },
    {
      path: '/user/orders',
      name: 'user-orders',
      component: () => import('../views/user/OrdersView.vue'),
      meta: {
        title: '訂單查詢',
        subtitle: '查看兌換訂單記錄'
      } as PageMeta
    },
    {
      path: '/user/orders/:txnSeq',
      name: 'user-order-detail',
      component: () => import('../views/user/OrderDetailView.vue'),
      meta: {
        title: '訂單詳情',
        subtitle: '訂單完整資訊與操作'
      } as PageMeta
    },
    {
      path: '/user/applications/vendor',
      name: 'user-vendor-application',
      component: () => import('../views/user/VendorApplicationView.vue'),
      meta: {
        title: '申請店家',
        subtitle: '填寫店家申請資料'
      } as PageMeta
    },
    {
      path: '/user/vendor-qrcode',
      name: 'user-vendor-qrcode',
      component: () => import('../views/user/VendorQRCodeView.vue'),
      beforeEnter: vendorRouteGuard,
      meta: {
        title: '店家 QRCode',
        subtitle: '產生店家專屬 QRCode'
      } as PageMeta
    },
    {
      path: '/user/scan-vendor-qr',
      name: 'user-scan-vendor-qr',
      component: () => import('../views/user/VendorQRScanView.vue'),
      meta: {
        title: '掃描店家 QRCode',
        subtitle: '掃描店家專屬 QRCode 進入店家頁面'
      } as PageMeta
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('../views/NotificationsView.vue'),
      meta: {
        title: '通知中心',
        subtitle: '查看系統通知與審核狀態'
      } as PageMeta
    },

    // Sample 頁面路由 - 展示 shared-components 使用範例
    {
      path: '/sample',
      name: 'sample',
      component: () => import('../components/layout/SampleLayout.vue'),
      redirect: '/sample/basic',
      meta: {
        title: 'Sample 示範',
        subtitle: 'UI 元件與頁面架構展示'
      } as PageMeta,
      children: [
        {
          path: 'basic',
          name: 'sample-basic',
          component: () => import('../views/sample/SampleBasicView.vue'),
          meta: {
            title: 'Sample 頁面架構說明',
            subtitle: '展示頁面 UI 架構配置及區域劃分',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: 'Sample 示範', to: '/sample' },
              { label: '頁面架構' }
            ],
            actions: [
              { label: '檢視原始碼', action: 'viewSource', type: 'outline', icon: 'file-text' },
              { label: '複製範例', action: 'copyExample', type: 'secondary', icon: 'clipboard' }
            ]
          } as PageMeta
        },
        {
          path: 'search',
          name: 'sample-search',
          component: () => import('../views/sample/SampleSearchView.vue'),
          meta: {
            title: 'Sample 查詢頁面',
            subtitle: '展示列表頁面 UI 架構與 SearchArea、ActionArea、DataTable 元件使用',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: 'Sample 示範', to: '/sample' },
              { label: '查詢頁面' }
            ],
            actions: [
              { label: '新增商品', action: 'create', type: 'primary', icon: 'plus' },
              { label: '匯出 Excel', action: 'export', type: 'outline', icon: 'download' },
              { label: '批次操作', action: 'bulkAction', type: 'secondary', icon: 'settings' }
            ]
          } as PageMeta
        },
        {
          path: 'form',
          name: 'sample-form',
          component: () => import('../views/sample/SampleFormView.vue'),
          meta: {
            title: 'Sample 表單頁面',
            subtitle: '展示表單頁面 UI 架構與 FormBody、ActionArea 元件使用',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: 'Sample 示範', to: '/sample' },
              { label: '表單頁面' }
            ],
            actions: [
              { label: '儲存', action: 'save', type: 'primary', icon: 'save' },
              { label: '儲存並繼續', action: 'saveAndContinue', type: 'success', icon: 'save' },
              { label: '預覽', action: 'preview', type: 'outline', icon: 'eye' },
              { label: '返回', action: 'back', type: 'secondary', icon: 'arrow-left' }
            ]
          } as PageMeta
        },
        {
          path: 'icon',
          name: 'sample-icon',
          component: () => import('../views/sample/SampleIconView.vue'),
          meta: {
            title: 'Sample Icon 元件',
            subtitle: '展示通用 Icon 元件支援的三種圖示庫：Lucide Icons、Heroicons 和 Iconify',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: 'Sample 示範', to: '/sample' },
              { label: 'Icon 元件' }
            ],
            actions: [
              { label: '查看文件', action: 'viewDocs', type: 'primary', icon: 'book-open' },
              { label: '複製程式碼', action: 'copyCode', type: 'outline', icon: 'clipboard' },
              { label: 'GitHub', action: 'github', type: 'secondary', icon: 'external-link' }
            ]
          } as PageMeta
        },

        {
          path: 'test-action-buttons',
          name: 'test-action-buttons',
          component: () => import('../views/sample/TestActionButtonsView.vue'),
          meta: {
            title: 'ActionButtons 測試',
            subtitle: '測試 ActionButtons 元件的圖標顯示功能',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: 'Sample 示範', to: '/sample' },
              { label: 'ActionButtons 測試' }
            ],
            actions: [
              { label: '重整頁面', action: 'refresh', type: 'primary', icon: 'refresh-cw' },
              { label: '返回示範', action: 'back', type: 'secondary', icon: 'arrow-left' }
            ]
          } as PageMeta
        },
        {
          path: 'loading-test',
          name: 'sample-loading-test',
          component: () => import('../views/sample/LoadingTestView.vue'),
          meta: {
            title: 'Loading 狀態測試',
            subtitle: '測試各種 Loading 狀態與 MessageService 功能',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: 'Sample 示範', to: '/sample' },
              { label: 'Loading 測試' }
            ],
            actions: [
              { label: '重新整理', action: 'refresh', type: 'primary', icon: 'refresh-cw' },
              { label: '查看 Loading Demo', action: 'viewDemo', type: 'outline', icon: 'eye' }
            ]
          } as PageMeta
        },
        {
          path: 'qrcode-scan-test',
          name: 'sample-qrcode-scan-test',
          component: () => import('../views/vendor/VendorScanTestView.vue'),
          meta: {
            title: 'QRCode 掃描測試',
            subtitle: '前端測試 - 僅顯示 QRCode 內容，不呼叫後端 API',
            breadcrumbs: [
              { label: '首頁', to: '/' },
              { label: 'Sample 示範', to: '/sample' },
              { label: 'QRCode 掃描測試' }
            ],
            actions: [
              { label: '查看真正頁面', action: 'viewReal', type: 'primary', icon: 'external-link' },
              { label: '重新整理', action: 'refresh', type: 'outline', icon: 'refresh-cw' }
            ]
          } as PageMeta
        },

      ],
    },
  ],
})

// 設定路由守衛
setupRouterGuards(router)

export default router
